import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "@/lib/toast";
import StepIndicator from "@/components/StepIndicator";
import PatientStep from "@/components/PatientStep";
import { useAuth } from "@/hooks/useAuth";
import FootageStep from "@/components/FootageStep";
import UpdateBanner from "@/components/UpdateBanner";
import AppHeader from "@/components/AppHeader";
import { trpc } from "@/lib/trpc";
import { saveHandle, loadHandle, deleteHandle } from "@/lib/sessionHandleStore";

export type PatientMode = "new" | "returning";

export interface WizardState {
  mode: PatientMode;
  /** Google Drive folder ID for the patient — no name stored in DB */
  driveFolderId: string;
  /** Display-only patient name (never persisted to DB) */
  patientName: string;
  dateFolder: string; // MM.DD.YY — empty when auto-detect-from-metadata is active
  /**
   * When true, " - exam" is appended to the MM.DD.YY folder name. The user
   * sets this on PatientStep; FootageStep applies it when materializing the
   * date folder(s) from file metadata.
   */
  isExam?: boolean;
  /**
   * Provider A-Roll only: free-text suffix appended to the MM.DD.YY folder
   * name, e.g. videoTopic="Primitive Reflexes" → "05.22.26 - Primitive
   * Reflexes". Set on PatientStep when subjectKind="provider"; FootageStep
   * sanitizes and applies it when materializing the date folder(s). Takes
   * precedence over isExam (provider workflow doesn't expose the exam toggle).
   */
  videoTopic?: string;
  /**
   * Which workflow this wizard run belongs to. "provider" flips two
   * behaviors in FootageStep + PatientStep: (1) Local Drive is required and
   * the folder must be picked fresh each session (no auto-recall from
   * sessionHandleStore), and (2) files save FLAT into the picked local
   * folder — no subject subfolder, no MM.DD.YY subfolder, no topic
   * subfolder. Google Drive is unaffected and still uses the full nested
   * path. Undefined / "patient" preserves the existing behavior.
   */
  subjectKind?: "patient" | "provider";
  /**
   * Associated provider NAME(s) chosen on PatientStep (patient workflow only,
   * Phase 3). Session-level: FootageStep stamps the same list onto every
   * footage row it creates (footage_files.providers), which feeds Footage
   * Search. Names, not IDs — the Provider library is the source of truth.
   */
  providers?: string[];
  /**
   * Who filmed this batch. Defaults to the logged-in user (set on
   * PatientStep from useAuth), but the user can attribute the batch to a
   * different staff member (e.g. Kyle uploading footage Chandler filmed).
   * FootageStep stamps these onto every footage row it creates
   * (footage_files.uploaded_by_*), which feeds the "Filmed by" filter in
   * Footage Search. googleId + denormalized display name.
   */
  filmedByUserId?: string;
  filmedByName?: string;
  destinations: string[]; // ["local", "google_drive"]
  sessionId: number | null;
  consentFormUploaded: boolean;
  /** Root folder handle chosen by user (e.g. "Patient Videos" drive folder) */
  localFolderHandle?: FileSystemDirectoryHandle | null;
  /** Display name of the root folder */
  localFolderPath?: string;
  /** Handle for the PatientName/ subfolder created inside the root folder */
  patientFolderHandle?: FileSystemDirectoryHandle | null;
  /** Stored label for the root folder (persisted to DB for returning patients) */
  localFolderLabel?: string;
  /** Google Drive folder ID chosen for this patient (the PatientName/ folder) */
  gDriveFolderId?: string;
  /** Human-readable breadcrumb path for the chosen Google Drive folder */
  gDriveFolderPath?: string;
  /** Google Drive folder ID for the MM.DD.YY/ date subfolder (created at proceed time) */
  gDriveDateFolderId?: string;
}

const STEPS = ["Patient Setup", "Upload Footage"];

export default function UploadWizard() {
  const [, navigate] = useLocation();
  // Redirect to login if not authenticated (e.g. after logout)
  useAuth({ redirectOnUnauthenticated: true });
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardState, setWizardState] = useState<WizardState>({
    mode: "returning",
    driveFolderId: "",
    patientName: "",
    dateFolder: "",
    destinations: ["local"],
    sessionId: null,
    consentFormUploaded: false,
    localFolderHandle: null,
    localFolderPath: "",
    patientFolderHandle: null,
    localFolderLabel: "",
  });

  const createSession = trpc.sessions.create.useMutation();

  // On mount: check if we're resuming a session ("Add More This Session" flow)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resume = params.get("resume");
    if (resume !== "1") return;

    const sessionId = parseInt(params.get("sessionId") ?? "", 10);
    const patientName = params.get("patient") ?? "";
    const dateFolder = params.get("date") ?? "";
    const destinations = (params.get("destinations") ?? "").split(",").filter(Boolean);
    const gDriveDateFolderId = params.get("driveFolderId") ?? undefined;
    const gDriveFolderId = params.get("gDriveFolderId") ?? undefined;
    const gDriveFolderPath = params.get("gDriveFolderPath") ?? undefined;
    const localFolderLabel = params.get("folder") ?? "";
    const driveFolderId = gDriveFolderId ?? "";

    // dateFolder is allowed to be empty here — the auto-detect-from-metadata
    // flow leaves it blank, and FootageStep handles per-file date routing on
    // resume the same way it does on a fresh upload. We only require the
    // session row + patient identifiers.
    if (!sessionId || !patientName) return;

    // Restore FileSystemDirectoryHandle from IndexedDB if available
    const restoreHandles = async () => {
      const patientFolderHandle = await loadHandle("resume_patientFolderHandle").catch(() => undefined);
      const localFolderHandle = await loadHandle("resume_localFolderHandle").catch(() => undefined);
      // Clean up after reading
      await deleteHandle("resume_patientFolderHandle").catch(() => {});
      await deleteHandle("resume_localFolderHandle").catch(() => {});

      setWizardState({
        mode: "returning",
        driveFolderId,
        patientName,
        dateFolder,
        destinations,
        sessionId,
        consentFormUploaded: false,
        localFolderHandle: localFolderHandle ?? null,
        localFolderPath: localFolderLabel,
        patientFolderHandle: patientFolderHandle ?? null,
        localFolderLabel,
        gDriveFolderId,
        gDriveFolderPath,
        gDriveDateFolderId,
      });
      setCurrentStep(1);
    };
    void restoreHandles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePatientStepComplete = async (state: Partial<WizardState>) => {
    const updated = { ...wizardState, ...state };
    setWizardState(updated);

    // Create the upload session. dateFolder is allowed to be empty in the
    // auto-detect-from-metadata flow — the actual MM.DD.YY value(s) are
    // resolved per-file in FootageStep and the session row just acts as the
    // patient-level FK parent for footage rows.
    if (!updated.driveFolderId) {
      toast.error("Missing patient information");
      return;
    }

    try {
      const result = await createSession.mutateAsync({
        driveFolderId: updated.driveFolderId,
        dateFolder: updated.dateFolder ?? "",
      });
      setWizardState((prev) => ({ ...prev, ...updated, sessionId: result.id }));
      setCurrentStep(1);
    } catch (err: any) {
      const msg = err?.message ?? String(err);
      console.error("[UploadWizard] createSession failed:", msg, err);
      toast.error(`Failed to create upload session: ${msg}`);
    }
  };

  const handleUploadComplete = async (processedFiles: ProcessedFile[]) => {
    const folderParam = wizardState.localFolderLabel
      ? `&folder=${encodeURIComponent(wizardState.localFolderLabel)}`
      : "";
    const destParam = wizardState.destinations.length > 0
      ? `&destinations=${encodeURIComponent(wizardState.destinations.join(","))}`
      : "";
    const driveFolderParam = wizardState.gDriveDateFolderId
      ? `&driveFolderId=${encodeURIComponent(wizardState.gDriveDateFolderId)}`
      : "";
    // Store thumbnail + status data in sessionStorage to avoid URL length limits.
    // The `id` is the footage_files row id — Confirmation polls
    // /api/finalize-analysis/status against it to show a per-file
    // "AI saved" indicator once Pegasus + Whisper + embedding land.
    try {
      const thumbnailData = processedFiles.map((f) => ({
        id: f.id,
        thumbnail: f.thumbnail,
        thumbnailWidth: f.thumbnailWidth,
        thumbnailHeight: f.thumbnailHeight,
        status: f.status,
        name: f.renamedName || f.originalName,
      }));
      sessionStorage.setItem("confirmation_thumbnails", JSON.stringify(thumbnailData));
    } catch {
      // sessionStorage unavailable — thumbnails will fall back to icon on confirmation page
    }
    // Save folder handles to IndexedDB so "Add More This Session" can restore them
    try {
      if (wizardState.patientFolderHandle) {
        await saveHandle("resume_patientFolderHandle", wizardState.patientFolderHandle);
      }
      if (wizardState.localFolderHandle) {
        await saveHandle("resume_localFolderHandle", wizardState.localFolderHandle);
      }
    } catch {
      // IDB unavailable — Add More This Session will work without local folder handle
    }

    const sessionIdParam = wizardState.sessionId ? `&sessionId=${wizardState.sessionId}` : "";
    const gDriveFolderIdParam = wizardState.gDriveFolderId ? `&gDriveFolderId=${encodeURIComponent(wizardState.gDriveFolderId)}` : "";
    const gDriveFolderPathParam = wizardState.gDriveFolderPath ? `&gDriveFolderPath=${encodeURIComponent(wizardState.gDriveFolderPath)}` : "";
    const skippedFiles = processedFiles.filter((f) => f.status === "skipped");
    const skippedParam = skippedFiles.length > 0 ? `&skipped=${skippedFiles.length}` : "";
    // Collect the distinct date folders the batch actually landed in. In
    // auto-detect mode there can be more than one (clips spanning multiple
    // days). Fall back to wizardState.dateFolder for the session-resume /
    // pre-set case where every file uses the same single folder.
    const resolvedDateFolders = Array.from(
      new Set(processedFiles.map((f) => f.dateFolder).filter((d): d is string => !!d)),
    ).sort();
    const effectiveDate =
      resolvedDateFolders.length === 1
        ? resolvedDateFolders[0]
        : wizardState.dateFolder || "";
    const datesParam =
      resolvedDateFolders.length > 1
        ? `&dates=${encodeURIComponent(resolvedDateFolders.join(","))}`
        : "";
    navigate(
      `/confirmation?patient=${encodeURIComponent(wizardState.patientName)}&date=${encodeURIComponent(effectiveDate)}${datesParam}&count=${processedFiles.length}${folderParam}${destParam}${driveFolderParam}${sessionIdParam}${gDriveFolderIdParam}${gDriveFolderPathParam}${skippedParam}&files=${encodeURIComponent(JSON.stringify(processedFiles.map((f) => f.renamedName || f.originalName)))}`
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Update notification banner (Electron desktop only) */}
      <UpdateBanner />

      <AppHeader />

      {/* Step Indicator */}
      <div className="bg-white border-b border-border">
        <div className="container py-4">
          <StepIndicator steps={STEPS} currentStep={currentStep} />
        </div>
      </div>

      {/* Main Content */}
      <main className="container py-8">
        <div className="max-w-2xl mx-auto">
          {currentStep === 0 && (
            <PatientStep
              wizardState={wizardState}
              onComplete={handlePatientStepComplete}
              isLoading={createSession.isPending}
            />
          )}
          {currentStep === 1 && wizardState.sessionId && (
            <FootageStep
              wizardState={wizardState}
              onComplete={handleUploadComplete}
              onBack={() => setCurrentStep(0)}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export interface ProcessedFile {
  id: number;
  originalName: string;
  renamedName: string | null;
  activities: string[];
  /** Google Drive file ID returned by the resumable upload (when applicable) */
  driveFileId?: string;
  /** "uploaded" | "skipped" | "drive_failed" | "failed" */
  status: string;
  /** First-frame thumbnail data URL (generated client-side) */
  thumbnail?: string;
  /** Natural video width for aspect-ratio-correct display */
  thumbnailWidth?: number;
  /** Natural video height for aspect-ratio-correct display */
  thumbnailHeight?: number;
  /** MM.DD.YY[ - exam] folder this file was actually routed into. With
   *  auto-detect-from-metadata, a single batch can span multiple date
   *  folders — Confirmation reads these to render the right breadcrumb(s). */
  dateFolder?: string;
}
